/**
 * Shape of a console assignment's input/output transcript.
 *
 * A transcript is the chronological record of an interactive console run:
 * each entry is either an output ("print") row or an input row (the user's
 * response, optionally preceded by a prompt). The same shape describes both
 * the expected transcript stored on a unit test and the observed transcript
 * captured at runtime.
 */

/**
 * A single visible line of console output.
 *
 * Lines never contain `\n` characters — `\n` is the *delimiter* between
 * lines, not part of any line's content. An empty string (`""`) is a valid
 * line (e.g., from `print()`).
 */
export type OutputLine = string;

/**
 * Kinds of entries that can appear in a console transcript:
 *
 * - `'print'`: the program emitted output on this row.
 * - `'input'`: the program requested input on this row; the user's response
 *   is recorded in `input`. The `output` field carries any prompt text
 *   printed before the input was requested.
 */
export const CONSOLE_TRANSCRIPT_ENTRY_KINDS = ['print', 'input'] as const;

/** Discriminator for a single entry in a console transcript. */
export type ConsoleTranscriptEntryKind =
  (typeof CONSOLE_TRANSCRIPT_ENTRY_KINDS)[number];

/**
 * Type guard for {@link ConsoleTranscriptEntryKind}.
 *
 * @param value Candidate value.
 * @return Whether `value` is a valid kind discriminator.
 */
export function isConsoleTranscriptEntryKind(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Accepts any input for runtime validation
  value: any,
): value is ConsoleTranscriptEntryKind {
  return (
    typeof value === 'string' &&
    CONSOLE_TRANSCRIPT_ENTRY_KINDS.includes(
      value as ConsoleTranscriptEntryKind,
    )
  );
}

/** A `'print'` entry: the program emitted output on this row. */
export interface ConsoleTranscriptPrintEntry {
  type: 'print';
  /** The output line printed on this row. */
  output: OutputLine;
}

/** An `'input'` entry: the program requested input on this row. */
export interface ConsoleTranscriptInputEntry {
  type: 'input';
  /** Prompt text printed before the input was requested; may be empty. */
  output: OutputLine;
  /** The user's response. */
  input: string;
}

/** A single entry in a console transcript. */
export type ConsoleTranscriptEntry =
  | ConsoleTranscriptPrintEntry
  | ConsoleTranscriptInputEntry;

/** A console transcript: an ordered sequence of entries. */
export type ConsoleTranscript = ConsoleTranscriptEntry[];

/**
 * Extracts a transcript's output lines as a parallel array indexed by row.
 *
 * For both `'print'` and `'input'` entries, the row's output is included
 * (for input entries this is the prompt printed before the input).
 *
 * Provided as a transitional adapter for consumers that still operate on
 * the legacy parallel-array shape; new code should iterate the transcript
 * directly.
 *
 * @param transcript Transcript to project.
 * @return One output line per transcript row, in order.
 */
export function getTranscriptOutputs(
  transcript: ConsoleTranscript,
): OutputLine[] {
  return transcript.map(entry => entry.output);
}

/**
 * Builds a transcript from the legacy parallel-array shape.
 *
 * Each output line becomes one entry. If the row index appears in `inputs`,
 * the entry is an `'input'` entry carrying that value; otherwise it is a
 * `'print'` entry.
 *
 * Provided as a transitional adapter for consumers that still produce the
 * parallel-array shape; new code should produce a transcript directly.
 *
 * @param outputs Output lines, one per row.
 * @param inputs User-supplied inputs keyed by row index.
 * @return The reconstructed transcript.
 */
export function transcriptFromOutputsAndInputs(
  outputs: OutputLine[],
  inputs: Record<number, string>,
): ConsoleTranscript {
  return outputs.map((line, idx) => {
    const inputValue = inputs[idx];
    if (inputValue !== undefined) {
      return {type: 'input', output: line, input: inputValue};
    }
    return {type: 'print', output: line};
  });
}

/**
 * Extracts a transcript's input values keyed by their row index.
 *
 * Only `'input'` entries appear in the result; `'print'` rows are skipped.
 * The key is the entry's position in the transcript array (not a count of
 * input entries).
 *
 * Provided as a transitional adapter for consumers that still operate on
 * the legacy parallel-array shape; new code should iterate the transcript
 * directly.
 *
 * @param transcript Transcript to project.
 * @return Map from transcript row index to user-supplied input.
 */
export function getTranscriptInputs(
  transcript: ConsoleTranscript,
): Record<number, string> {
  return transcript.reduce<Record<number, string>>((acc, entry, idx) => {
    if (entry.type === 'input') {
      acc[idx] = entry.input;
    }
    return acc;
  }, {});
}
